import router from '@/router'
import type {
  ModuleI,
  GlobModuleI,
  ServiceObjectI,
  StoreObjectI,
} from '@/typescript/types/abstracts/plugins/ModuleTypes'
import { App, Plugin } from 'vue'
import type { Router } from 'vue-router'
import { storeRegistry, type StoreFactory } from '@/plugins/storeRegistry'

export let registerServices: ServiceObjectI
export let registerStore: StoreObjectI
const registerModule = (
  name: string,
  module: {
    router?: (router: Router) => void
    service?: object
    store?: object
  },
) => {
  if (module.store) {
    registerStore = {
      ...registerStore,
      [name]: module.store,
    }
    storeRegistry[name] = module.store as StoreFactory
  }
  if (module.service) {
    registerServices = {
      ...registerServices,
      [name]: module.service,
    }
  }
  if (module.router) {
    module.router(router)
  }
}
export const registerModules = (modules: ModuleI) => {
  Object.keys(modules).forEach((keyValue: string) => {
    const module: {
      router?: (router: Router) => void
      service?: object
      store?: object
    } = modules[keyValue]
    registerModule(keyValue, module)
  })
}

function initGlobalModules() {
  const service = import.meta.glob('../services/global.ts', { eager: true }) as GlobModuleI
  const store = import.meta.glob('../store/global.ts', { eager: true }) as Record<
    string,
    { useGlobalStore: StoreFactory }
  >
  const serviceInit = Object.keys(service).map(key => service[key].default)[0]
  return {
    service: serviceInit,
    // global.ts exposes the store as a named export (no default), so reach into
    // the module namespace for the factory itself — otherwise storeRegistry holds
    // the namespace object and resetAllStores() calls a non-function.
    store: Object.values(store)[0].useGlobalStore,
  }
}

const initRegistrationModules: Plugin<[]> = {
  async install(app: App) {
    const moduleList = import.meta.glob('../modules/**/index.ts', { eager: true }) as GlobModuleI
    const featureModulesList = import.meta.glob('../featureModules/**/index.ts', { eager: true }) as GlobModuleI
    const allModules = {
      ...moduleList,
      ...featureModulesList,
    }
    const modules: { [key: string]: object | ModuleI } = {}
    Object.entries(allModules).forEach(([key, module]) => {
      const moduleString = key.split('/')
      const moduleName = moduleString[moduleString.length - 2].split('.')[0]
      modules[moduleName] = module.default
    })
    modules.global = initGlobalModules()
    registerModules(modules)
    app.config.globalProperties.$services = registerServices
    app.config.globalProperties.$stores = registerStore
  },
}

export default initRegistrationModules
